// filterPanel.js
import {
    defineComponent,
    ref,
    computed,
    watch,
    inject,
    onMounted,
    onUnmounted,
} from "/Scripts/vue.esm-browser.js";
import BudgetFilter from "/Component/Shop/filter/BudgetFilter.js";
import DatepickerFilter from "/Component/Shop/filter/DatepickerFilter.js";
const html = String.raw;
export default defineComponent({
    name: "FilterPanel",
    components: {
        BudgetFilter,
        DatepickerFilter,
    },
    props: {
        filters: {
            type: Object,
            required: true,
        },
        groupData: {
            type: Array,
            required: true,
        },
        isFilterOpen: {
            type: Boolean,
            required: true,
        },
        isSearch: {
            type: Boolean,
            default: false
        },
    },
    emits: ["update:filters", "reset-filters", "toggle-filter"],
    setup(props, { emit }) {
        const filterContainer = ref(null);
        const filterCount = inject("filterCount");
        // Computed properties for dynamic data
        const priceRangeMin = computed(() =>
            props.groupData
                .flatMap((group) =>
                    group.groups.flatMap((item) => item.sitePrice)
                )
                .reduce((min, price) => Math.min(min, price), Infinity)
        );
        const priceRangeMax = computed(() =>
            props.groupData
                .flatMap((group) =>
                    group.groups.flatMap((item) => item.sitePrice)
                )
                .reduce((max, price) => Math.max(max, price), -Infinity)
        );
        const dateRangeStart = computed(
            () =>
                props.groupData
                    .flatMap((group) =>
                        group.groups.flatMap((item) => item.leaveDate)
                    )
                    .sort()[0]
        );
        const dateRangeEnd = computed(
            () =>
                props.groupData
                    .flatMap((group) =>
                        group.groups.flatMap((item) => item.leaveDate)
                    )
                    .sort()[
                    props.groupData.flatMap((group) =>
                        group.groups.flatMap((item) => item.leaveDate)
                    ).length - 1
                ]
        );

        // Count items for each filter option
        const countItems = (key, value) => {
            return props.groupData.reduce((total, group) => {
                // Count how many groups within this group match the criteria
                const matchingGroupsCount = group.groups.filter((item) => {
                    if (key === "groupDay") {
                        return (
                            item.groupDay <= value.max &&
                            item.groupDay >= value.min
                        );
                    }
                    if (key === "airline") {
                        return item.airline === value;
                    }
                    if (key === "leaveDate") {
                        const day = new Date(item.leaveDate).getDay();
                        return value === day;
                    }
                    if (key === "hotType") {
                        return item.hotType === value;
                    }
                    if (key === "remainSeat") {
                        return item.remainSeat <= value;
                    }
                    return false;
                }).length;

                return total + (matchingGroupsCount > 0 ? 1 : 0);
            }, 0);
        };
        const airlines = computed(() => {
            // Get all unique airline codes first
            const allAirlineCodes = props.groupData
                .flatMap((group) => group.groups)
                .flatMap((item) => item.airlineCodes)
                .filter((item) => item);
            const uniqueAirlines = [...new Set(allAirlineCodes)];

            // Count groups containing each airline code
            const airlineGroupCount = {};

            uniqueAirlines.forEach((airline) => {
                // For each airline, count how many group.groups entries contain it
                const count = props.groupData.reduce((total, group) => {
                    // Count matching groups in this parent group
                    const matchingGroups = group.groups.filter((item) =>
                        item.airlineCodes.includes(airline)
                    ).length;

                    return total + (matchingGroups > 0 ? 1 : 0);
                }, 0);

                airlineGroupCount[airline] = count;
            });

            // Sort airline codes by their group count (descending)
            return uniqueAirlines.sort(
                (a, b) => airlineGroupCount[b] - airlineGroupCount[a]
            );
        });

        const tags = computed(() => {
            // Get all unique airline codes first
            const allTags = props.groupData.flatMap(
                (group) => group.hashTagList
            );
            const uniqueTags = [...new Set(allTags)];
            return uniqueTags;
        });

        const availableWeekdays = computed(() => {
            const days = [
                "星期日",
                "星期一",
                "星期二",
                "星期三",
                "星期四",
                "星期五",
                "星期六",
            ];
            const weekdays = [
                "星期一",
                "星期二",
                "星期三",
                "星期四",
                "星期五",
                "星期六",
                "星期日",
            ];
            return weekdays.filter((day) => {
                const dayIndex = days.indexOf(day);
                return props.groupData.some((group) =>
                    group.groups.some((item) => {
                        const itemDay = new Date(item.leaveDate).getDay();
                        return itemDay === dayIndex;
                    })
                );
            });
        });

        const availableDayRanges = computed(() => {
            return props.filters.dayRanges
                .map((range, index) => ({ ...range, originalIndex: index }))
                .filter((range) => {
                    return props.groupData.some((group) =>
                        group.groups.some((item) => {
                            const days = item.groupDay;

                            if (range.rangeStart === 0) {
                                return days <= range.rangeEnd;
                            } else if (range.rangeEnd === 0) {
                                return days >= range.rangeStart;
                            } else {
                                return days >= range.rangeStart && days <= range.rangeEnd;
                            }
                        })
                    );
                });
        });

        // Handle filter toggle
        const toggleFilter = () => {
            emit("toggle-filter");
        };

        // Handle price range update (simplified, you might want to use a slider library)
        const updatePriceRange = ({ min, max }) => {
            emit("update:filters", {
                ...props.filters,
                priceRange: [min, max],
            });
        };

        // Handle date range update
        const updateDateRange = (start, end) => {
            emit("update:filters", {
                ...props.filters,
                dateRange: [start, end],
            });
        };

        // Handle checkbox changes
        const updateCheckbox = (key, value, checked) => {
            const newFilters = { ...props.filters };
            if (key === "groupStatus") {
                newFilters.groupStatus[value] = checked;
            } else if (key === "selectedWeekdays") {
                checked
                    ? newFilters.selectedWeekdays.add(value)
                    : newFilters.selectedWeekdays.delete(value);
            } else if (key === "dayRanges") {
                // dayRanges 現在是陣列，value 是 index
                const updatedDayRanges = [...newFilters.dayRanges];
                updatedDayRanges[value] = {
                    ...updatedDayRanges[value],
                    active: checked,
                };
                newFilters.dayRanges = updatedDayRanges;
            } else if (key === "featureTags") {
                checked
                    ? newFilters.featureTags.add(value)
                    : newFilters.featureTags.delete(value);
            } else if (key === "selectedAirlines") {
                checked
                    ? newFilters.selectedAirlines.add(value)
                    : newFilters.selectedAirlines.delete(value);
            }
            emit("update:filters", newFilters);
        };

        // Reset all filters
        const resetFilters = () => {
            emit("reset-filters");
        };
        const closePopup = () => {
            if (document.querySelector(".datePicker_popup")) {
                document.querySelector(".datePicker_popup").style.display =
                    "";
            }
        };
        onMounted(() => {
            // 監聽全頁面的滾動
            window.addEventListener('scroll', closePopup);

            // 監聽 filter_container 的滾動
            if (filterContainer.value) {
                filterContainer.value.addEventListener('scroll', closePopup);
            }
        });

        onUnmounted(() => {
            window.removeEventListener('scroll', closePopup);

            if (filterContainer.value) {
                filterContainer.value.removeEventListener('scroll', closePopup);
            }
        });

        // Watch for filter changes to update UI (optional)
        watch(
            () => props.filters,
            (newFilters) => {
                // Update UI elements if needed
            },
            { deep: true }
        );

        return {
            toggleFilter,
            priceRangeMin,
            priceRangeMax,
            dateRangeStart,
            dateRangeEnd,
            updatePriceRange,
            updateDateRange,
            updateCheckbox,
            resetFilters,
            countItems,
            airlines,
            tags,
            availableWeekdays,
            availableDayRanges,
            filterCount,
            filterContainer
        };
    },
    template: html`
        <div class="leftsidebar" :class="{'close':!isFilterOpen}">
            <!-- Date Picker Popup -->
                <DatepickerFilter
                    :dateRangeStart="dateRangeStart"
                    :dateRangeEnd="dateRangeEnd"
                    :dateRange="filters.dateRange"
                    @update:updateDateRange="updateDateRange"
                />

            <div class="filter_container" ref="filterContainer" v-show="groupData.length>0">
                <button class="clean_btn" v-if="filterCount>0" @click="resetFilters">清除篩選</button>
                <!-- Filter Toggle Button -->
                <button class="filter_toggle_btn" @click="toggleFilter">
                    <svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M16.2893 1.93018L9.90016 8.21803L16.2389 14.5567L14.819 15.954L7.07176 8.20673L14.8807 0.521583L16.2893 1.93018ZM9.21828 1.90193L2.82915 8.18978L9.16784 14.5285L7.74804 15.9258L0.000748113 8.17848L7.80968 0.493337L9.21828 1.90193Z"
                            fill="#727272"
                        />
                    </svg>
                    <span>{{ isFilterOpen ? '收起' : '展開' }}</span>
                </button>

                <!-- Filter Content -->
                <div class="filter_content-close">
                    <div class="filterView-text">
                        <img src="/Content/Img/Shop/icon_filter-black.png" alt="" />
                        <span class="filterView-text-text">篩選</span>
                        <span class="filterView-number">
                            {{ filterCount > 9 ? '9+' : filterCount }}
                        </span>
                    </div>
                    <div class="filterView_container">
                        <div class="filterView filterView-calender">
                            <img src="/Content/Img/Shop/icon_calendar-gray.png" alt="" />
                            <span>日期</span>
                        </div>
                        <div class="filterView filterView-money">
                            <img src="/Content/Img/Shop/icon_money-gray.png" alt="" />
                            <span>預算</span>
                        </div>
                        <div class="filterView filterView-sell">
                            <img src="/Content/Img/Shop/icon_sell-gray.png" alt="" />
                            <span>特色</span>
                        </div>
                    </div>
                </div>

                <div
                    class="filter_content scrollbarWidth dragscroll"
                    v-show="isFilterOpen"
                >
                    <!-- Budget Filter -->
                    <BudgetFilter
                        :key="(priceRangeMin + priceRangeMax)||'loading' "
                        :minValue="priceRangeMin"
                        :maxValue="priceRangeMax"
                        :priceRange="filters.priceRange"
                        @update:range="updatePriceRange"
                    />

                    <!-- Departure Date Filter -->
                    <div class="filterbox filterbox-date">
                        <div class="filter_title">出發日期</div>
                        <div class="filterSpace_input">
                            <div class="input_bar">
                                <button class="input_bar_date-start">
                                    {{ filters.dateRange[0] }}
                                </button>
                                <span>~</span>
                                <button class="input_bar_date-end">
                                    {{ filters.dateRange[1] }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Group Status Filter -->
                    <div class="filterbox state">
                        <div class="filter_title">出團狀態</div>
                        <div class="filterSpace_checkbox">
                            <div class="filter_checkbox">
                                <input
                                    class="checkbox_input"
                                    type="checkbox"
                                    id="Ws-1"
                                    v-model="filters.groupStatus.guaranteed"
                                    @change="updateCheckbox('groupStatus', 'guaranteed', $event.target.checked)"
                                />
                                <label class="checkbox_label" for="Ws-1">
                                    <div class="checkbox_textBox">
                                        <span>保證出發</span>
                                    </div>
                                </label>
                            </div>
                            <!--<div class="filter_checkbox">
                                <input
                                    class="checkbox_input"
                                    type="checkbox"
                                    id="Ws-2"
                                    v-model="filters.groupStatus.forming"
                                    @change="updateCheckbox('groupStatus', 'forming', $event.target.checked)"
                                />
                                <label class="checkbox_label" for="Ws-2">
                                    <div class="checkbox_textBox">
                                        <span>即將成團</span>
                                    </div>
                                </label>
                            </div>-->
                        </div>
                    </div>

                    <!-- Departure Weekday Filter -->
                    <div class="filterbox">
                        <div class="filter_title">出發星期</div>
                        <div class="filterSpace_checkbox">
                            <div
                                class="filter_checkbox"
                                v-for="day in availableWeekdays"
                                :key="day"
                            >
                                <input
                                    class="checkbox_input"
                                    type="checkbox"
                                    :id="'Ww-' + day"
                                    :checked="filters.selectedWeekdays.has(dayIndex(day))"
                                    @change="updateCheckbox('selectedWeekdays', dayIndex(day), $event.target.checked)"
                                />
                                <label
                                    class="checkbox_label"
                                    :for="'Ww-' + day"
                                >
                                    <div class="checkbox_textBox">
                                        <span>{{ day }}</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Travel Days Filter -->
                    <div class="filterbox">
                        <div class="filter_title">旅遊天數</div>
                        <div class="filterSpace_checkbox">
                            <div
                                class="filter_checkbox"
                                v-for="(range, index) in availableDayRanges"
                                :key="range.originalIndex"
                            >
                                <input
                                    class="checkbox_input"
                                    type="checkbox"
                                    :id="'Wd-' + range.originalIndex"
                                    :checked="range.active"
                                    @change="updateCheckbox('dayRanges', range.originalIndex, $event.target.checked)"
                                />
                                <label
                                    class="checkbox_label"
                                    :for="'Wd-' + range.originalIndex"
                                >
                                    <div class="checkbox_textBox">
                                        <span>
                                            {{
                                                range.rangeStart === 0
                                                    ? range.rangeEnd + '天以下'
                                                    : range.rangeEnd === 0
                                                        ? range.rangeStart + '天以上'
                                                        : range.rangeStart + '~' + range.rangeEnd + '天'
                                            }}
                                        </span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Feature Tags Filter -->
                    <div class="filterbox" v-if="tags.length > 0">
                        <div class="filter_title">特色標籤</div>
                        <div class="filterSpace_tags">
                            <div
                                class="filter_tag"
                                v-for="tag in tags"
                                :key="tag"
                            >
                                <input
                                    class="checkbox_input"
                                    type="checkbox"
                                    :id="'We-' + tag"
                                    :checked="filters.featureTags.has(tag)"
                                    @change="updateCheckbox('featureTags', tag, $event.target.checked)"
                                />
                                <label class="tag_label" :for="'We-' + tag">
                                    <div class="tag_text">
                                        <span>{{ tag }}</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Airlines Filter -->
                    <div class="filterbox">
                        <div class="filter_title">航空公司</div>
                        <div class="filterSpace_checkbox">
                            <div
                                class="filter_checkbox"
                                v-for="airline in airlines"
                                :key="airline"
                            >
                                <input
                                    class="checkbox_input"
                                    type="checkbox"
                                    :id="'Wf-' + airline"
                                    :checked="filters.selectedAirlines.has(airline)"
                                    @change="updateCheckbox('selectedAirlines', airline, $event.target.checked)"
                                />
                                <label
                                    class="checkbox_label"
                                    :for="'Wf-' + airline"
                                >
                                    <div class="checkbox_textBox">
                                        <span>{{ airlineName(airline) }}</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    methods: {
        dayIndex(day) {
            const days = [
                "星期日",
                "星期一",
                "星期二",
                "星期三",
                "星期四",
                "星期五",
                "星期六",
            ];
            return days.indexOf(day);
        },
        airlineName(code) {
            return transService.airlineMap[code];
        },
    },
});
